'use client';

import { useState, useEffect, useRef, useMemo } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Eye, EyeOff, Truck, ArrowRight, Loader2, Package, Radio, Building2, KeyRound } from 'lucide-react';
import Image from 'next/image';
import { useAuth } from '@/contexts/AuthContext';
import { createClient, isAuthInProgress } from '@/lib/supabase/client';
import { Suspense } from 'react';


type AuthMode = 'login' | 'signup' | 'reset';

interface LoginForm {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface SignupForm {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
  companyName: string;
  agreeTerms: boolean;
}

interface ResetForm {
  email: string;
}

function SignUpLoginScreenInner() {
  const [mode, setMode] = useState<AuthMode>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const router = useRouter();
  const searchParams = useSearchParams();
  const { signIn, signUp, session, loading: authLoading, authLoading: authInFlight } = useAuth();
  // Memoize supabase client — prevents a new client being created on every render
  const supabase = useMemo(() => createClient(), []);
  // Prevent double-redirect if session check fires multiple times
  const isRedirecting = useRef(false);

  // Redirect already-authenticated users to dashboard (runs once after auth loads)
  useEffect(() => {
    if (!authLoading && session && !isRedirecting.current) {
      isRedirecting.current = true;
      const redirect = searchParams?.get('redirect') || '/dashboard';
      router.replace(redirect);
    }
  }, [authLoading, session, router, searchParams]);

  // Pre-fill invite email if coming from invite link
  const inviteToken = searchParams?.get('invite');
  const inviteEmail = searchParams?.get('email');

  useEffect(() => {
    if (inviteToken && inviteEmail) {
      setMode('signup');
      signupForm.setValue('email', decodeURIComponent(inviteEmail));
    }
  }, [inviteToken, inviteEmail]);

  const loginForm = useForm<LoginForm>({
    defaultValues: { email: '', password: '', rememberMe: false },
  });

  const signupForm = useForm<SignupForm>({
    defaultValues: { fullName: '', email: '', password: '', confirmPassword: '', companyName: '', agreeTerms: false },
  });

  const resetForm = useForm<ResetForm>({
    defaultValues: { email: '' },
  });

  const handleLogin = async (data: LoginForm) => {
    // Block if another auth request is already running (prevents lock contention)
    if (isAuthInProgress() || isLoading || authInFlight) return;
    setIsLoading(true);
    try {
      await signIn(data.email, data.password);
      toast.success('Welcome back!');
      router.push('/dashboard');
      router.refresh();
    } catch (error: any) {
      const msg = error?.message || '';
      const msgLower = msg.toLowerCase();
      if (
        msgLower.includes('already in progress') ||
        msgLower.includes('rate limit') ||
        msgLower.includes('too many requests') ||
        msgLower.includes('wait a moment') ||
        msgLower.includes('over_email_send_rate_limit') ||
        error?.status === 429
      ) {
        toast.error('Too many login attempts. Please wait a moment and try again.');
      } else if (
        msgLower.includes('lock broken') ||
        msgLower.includes('lock_broken') ||
        msgLower.includes('steal') ||
        msgLower.includes('refresh token not found') ||
        msgLower.includes('refresh_token_not_found')
      ) {
        toast.error('Session error detected. Your session has been cleared. Please try signing in again.');
      } else if (
        msgLower.includes('invalid login') ||
        msgLower.includes('invalid credentials') ||
        msgLower.includes('email not confirmed')
      ) {
        toast.error('Invalid email or password. Please check your credentials.');
      } else {
        toast.error(msg || 'Login failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async (data: SignupForm) => {
    if (data.password !== data.confirmPassword) {
      signupForm.setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    setIsLoading(true);
    try {
      await signUp(data.email, data.password, {
        fullName: data.fullName,
        companyName: data.companyName,
      });

      if (inviteToken) {
        try {
          await supabase
            .from('user_invites')
            .update({ status: 'Accepted', accepted_at: new Date().toISOString() })
            .eq('token', inviteToken)
            .eq('email', data.email);
        } catch {
          // Silent
        }
      }

      toast.success('Account created! Signing you in...');
      router.push('/dashboard');
      router.refresh();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to create account. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async (data: ResetForm) => {
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(data.email, {
        redirectTo: `${window.location.origin}/auth/callback?type=recovery`,
      });
      if (error) throw error;
      setResetSent(true);
      toast.success('Password reset email sent! Check your inbox.');
    } catch (error: any) {
      toast.error(error?.message || 'Failed to send reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const features = [
    { icon: Package, label: 'Load Management', desc: 'Create, track, and invoice every load' },
    { icon: Truck, label: 'Carrier Database', desc: 'MC/DOT compliance, insurance tracking' },
    { icon: Radio, label: 'Live Dispatch Board', desc: 'Real-time driver check-ins and ETAs' },
    { icon: Building2, label: 'Shipper CRM', desc: 'Manage shipper relationships and history' },
  ];

  return (
    <div className="min-h-screen flex">
      {/* Left panel — Branding */}
      <div
        className="hidden lg:flex lg:w-[52%] xl:w-[55%] flex-col justify-between p-10 xl:p-14 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, rgb(17 12 34) 0%, rgb(46 16 101) 60%, rgb(109 40 217) 100%)' }}
      >
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-[-20%] right-[-10%] w-96 h-96 rounded-full bg-violet-600/20 blur-3xl" />
          <div className="absolute bottom-[-10%] left-[-5%] w-80 h-80 rounded-full bg-violet-800/20 blur-3xl" />
          <div className="absolute top-[40%] left-[30%] w-64 h-64 rounded-full bg-indigo-600/10 blur-3xl" />
        </div>

        {/* Top brand mark */}
        <div className="flex items-center gap-3 relative z-10">
          <div className="w-10 h-10 rounded-xl overflow-hidden bg-white/10 border border-white/20 flex items-center justify-center flex-shrink-0">
            <Image
              src="/assets/images/logo-1773535505109.PNG"
              alt="JTI Freight LLC truck logo"
              width={40}
              height={40}
              className="object-contain w-full h-full"
              priority
            />
          </div>
          <div>
            <span className="text-base font-bold text-white tracking-tight leading-none block">J T I Freight LLC</span>
            <span className="text-[10px] text-violet-300/80 tracking-widest uppercase">Freight Management</span>
          </div>
        </div>

        {/* Center hero — logo + headline */}
        <div className="relative z-10 flex flex-col items-start">

          <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight mb-4">
            Your freight ops,<br />
            <span className="text-violet-300">fully in control.</span>
          </h1>
          <p className="text-white/60 text-lg mb-10 max-w-md">
            The CRM and TMS built for freight brokers who move fast. Manage loads, carriers, and dispatch in one place.
          </p>

          <div className="grid grid-cols-1 gap-4">
            {features.map(({ icon: FeatureIcon, label, desc }) => (
              <div key={label} className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-violet-600/30 border border-violet-500/30 flex items-center justify-center flex-shrink-0">
                  <FeatureIcon size={18} className="text-violet-300" />
                </div>
                <div>
                  <p className="text-white text-sm font-semibold">{label}</p>
                  <p className="text-white/50 text-xs">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10">
          <p className="text-white/30 text-xs">© 2026 J T I Freight LLC. All rights reserved.</p>
        </div>
      </div>

      {/* Right panel — Form */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-10 bg-gray-50">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <div className="w-10 h-10 rounded-xl overflow-hidden bg-violet-900 border border-violet-700 flex items-center justify-center flex-shrink-0">
              <Image
                src="/assets/images/logo-1773535505109.PNG"
                alt="JTI Freight LLC truck logo"
                width={40}
                height={40}
                className="object-contain w-full h-full"
                priority
              />
            </div>
            <div>
              <span className="text-base font-bold text-gray-900 leading-none block">J T I Freight LLC</span>
              <span className="text-[10px] text-violet-600 tracking-widest uppercase">Freight Management</span>
            </div>
          </div>

          {/* Tab toggle — only show for login/signup */}
          {mode !== 'reset' && (
            <div className="flex bg-gray-200 rounded-xl p-1 mb-8">
              <button
                onClick={() => setMode('login')}
                className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  mode === 'login' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Sign In
              </button>
              <button
                onClick={() => setMode('signup')}
                className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  mode === 'signup' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Create Account
              </button>
            </div>
          )}

          {/* Login Form */}
          {mode === 'login' && (
            <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-5">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Welcome back</h2>
                <p className="text-gray-500 text-sm mt-1">Sign in to your JTI Freight account</p>
              </div>

              <div>
                <label className="form-label">Email address</label>
                <input
                  type="email"
                  className="form-input"
                  placeholder="you@company.com"
                  {...loginForm.register('email', {
                    required: 'Email is required',
                    pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' },
                  })}
                />
                {loginForm.formState.errors.email && (
                  <p className="form-error">{loginForm.formState.errors.email.message}</p>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="form-label mb-0">Password</label>
                  <button
                    type="button"
                    onClick={() => setMode('reset')}
                    className="text-xs text-violet-600 hover:text-violet-700 font-medium"
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="form-input pr-10"
                    placeholder="••••••••"
                    {...loginForm.register('password', {
                      required: 'Password is required',
                      minLength: { value: 6, message: 'Minimum 6 characters' },
                    })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {loginForm.formState.errors.password && (
                  <p className="form-error">{loginForm.formState.errors.password.message}</p>
                )}
              </div>

              <div className="flex items-center gap-2">
                <input
                  id="rememberMe"
                  type="checkbox"
                  className="w-4 h-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                  {...loginForm.register('rememberMe')}
                />
                <label htmlFor="rememberMe" className="text-sm text-gray-600 select-none cursor-pointer">
                  Remember me for 30 days
                </label>
              </div>

              <button
                type="submit"
                disabled={isLoading || authInFlight}
                className="btn-primary w-full justify-center py-2.5 text-base"
              >
                {isLoading || authInFlight ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Signing in...
                  </>
                ) : (
                  <>
                    Sign In
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            </form>
          )}

          {/* Password Reset Form */}
          {mode === 'reset' && (
            <div className="space-y-5">
              <div>
                <button
                  onClick={() => { setMode('login'); setResetSent(false); }}
                  className="text-xs text-violet-600 hover:text-violet-700 font-medium mb-4 flex items-center gap-1"
                >
                  ← Back to Sign In
                </button>
                <h2 className="text-2xl font-bold text-gray-900">Reset password</h2>
                <p className="text-gray-500 text-sm mt-1">Enter your email and we'll send you a reset link</p>
              </div>

              {resetSent ? (
                <div className="bg-green-50 border border-green-200 rounded-xl p-5 text-center">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                    <KeyRound size={22} className="text-green-600" />
                  </div>
                  <p className="text-sm font-semibold text-green-800 mb-1">Reset email sent!</p>
                  <p className="text-xs text-green-600">Check your inbox for a password reset link. It may take a few minutes to arrive.</p>
                  <button
                    onClick={() => { setMode('login'); setResetSent(false); }}
                    className="mt-4 text-xs text-violet-600 hover:text-violet-700 font-medium"
                  >
                    Return to Sign In
                  </button>
                </div>
              ) : (
                <form onSubmit={resetForm.handleSubmit(handlePasswordReset)} className="space-y-4">
                  <div>
                    <label className="form-label">Email address</label>
                    <input
                      type="email"
                      className="form-input"
                      placeholder="you@company.com"
                      {...resetForm.register('email', {
                        required: 'Email is required',
                        pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' },
                      })}
                    />
                    {resetForm.formState.errors.email && (
                      <p className="form-error">{resetForm.formState.errors.email.message}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="btn-primary w-full justify-center py-2.5 text-base"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Reset Link
                        <ArrowRight size={18} />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          )}

          {/* Signup Form */}
          {mode === 'signup' && (
            <form onSubmit={signupForm.handleSubmit(handleSignup)} className="space-y-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Create your account</h2>
                <p className="text-gray-500 text-sm mt-1">Start managing your freight operations today</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Full name</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Julius Davis"
                    {...signupForm.register('fullName', { required: 'Full name is required' })}
                  />
                  {signupForm.formState.errors.fullName && (
                    <p className="form-error">{signupForm.formState.errors.fullName.message}</p>
                  )}
                </div>
                <div>
                  <label className="form-label">Company name</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="JTI Freight LLC"
                    {...signupForm.register('companyName', { required: 'Company name is required' })}
                  />
                  {signupForm.formState.errors.companyName && (
                    <p className="form-error">{signupForm.formState.errors.companyName.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="form-label">Work email</label>
                <input
                  type="email"
                  className="form-input"
                  placeholder="you@company.com"
                  {...signupForm.register('email', {
                    required: 'Email is required',
                    pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' },
                  })}
                />
                {signupForm.formState.errors.email && (
                  <p className="form-error">{signupForm.formState.errors.email.message}</p>
                )}
              </div>

              <div>
                <label className="form-label">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="form-input pr-10"
                    placeholder="Min. 8 characters"
                    {...signupForm.register('password', {
                      required: 'Password is required',
                      minLength: { value: 8, message: 'Minimum 8 characters' },
                    })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {signupForm.formState.errors.password && (
                  <p className="form-error">{signupForm.formState.errors.password.message}</p>
                )}
              </div>

              <div>
                <label className="form-label">Confirm password</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    className="form-input pr-10"
                    placeholder="Repeat your password"
                    {...signupForm.register('confirmPassword', { required: 'Please confirm your password' })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {signupForm.formState.errors.confirmPassword && (
                  <p className="form-error">{signupForm.formState.errors.confirmPassword.message}</p>
                )}
              </div>

              <div className="flex items-start gap-2">
                <input
                  id="agreeTerms"
                  type="checkbox"
                  className="w-4 h-4 mt-0.5 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                  {...signupForm.register('agreeTerms', { required: 'You must agree to the terms' })}
                />
                <label htmlFor="agreeTerms" className="text-sm text-gray-600 cursor-pointer">
                  I agree to the{' '}
                  <a href="#" className="text-violet-600 hover:underline">Terms of Service</a>
                  {' '}and{' '}
                  <a href="#" className="text-violet-600 hover:underline">Privacy Policy</a>
                </label>
              </div>
              {signupForm.formState.errors.agreeTerms && (
                <p className="form-error">{signupForm.formState.errors.agreeTerms.message}</p>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full justify-center py-2.5 text-base"
              >
                {isLoading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Creating account...
                  </>
                ) : (
                  <>
                    Create Account
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SignUpLoginScreen() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-gray-50 flex items-center justify-center"><div className="w-8 h-8 border-4 border-violet-600 border-t-transparent rounded-full animate-spin" /></div>}>
      <SignUpLoginScreenInner />
    </Suspense>
  );
}